/**
 * The Fias Forge test window's banner (ADR-023 D4, A16): says the build is
 * private, and lets the owner choose how calls that cost credits behave.
 *
 * - Live (the default): AI, image and audio calls are real and use the
 *   owner's credits, exactly as the published arche will.
 * - Mock: those calls are answered in this browser from the sample answers
 *   Claude wrote for the build (or a placeholder) — free, never sent.
 *
 * Saved data is the private test space's either way. The choice is remembered
 * per arche in this browser (a viewer convenience; nothing depends on it).
 */
import { useCallback, useEffect, useState } from 'react';
import { cn } from '@/lib/utils';

export type TestChannelMode = 'live' | 'mock';

const STORAGE_PREFIX = 'fias.testChannelMode.';

function readStoredMode(archeId: string): TestChannelMode {
  try {
    return window.localStorage.getItem(STORAGE_PREFIX + archeId) === 'mock' ? 'mock' : 'live';
  } catch {
    return 'live';
  }
}

/** The owner's Live/Mock choice for this arche's test window, remembered in this browser. */
export function useTestChannelMode(
  archeId: string,
): [TestChannelMode, (mode: TestChannelMode) => void] {
  const [mode, setModeState] = useState<TestChannelMode>(() => readStoredMode(archeId));
  useEffect(() => setModeState(readStoredMode(archeId)), [archeId]);
  const setMode = useCallback(
    (next: TestChannelMode) => {
      setModeState(next);
      try {
        window.localStorage.setItem(STORAGE_PREFIX + archeId, next);
      } catch {
        // Private window or blocked storage: the choice lasts for this page.
      }
    },
    [archeId],
  );
  return [mode, setMode];
}

export interface TestChannelBannerProps {
  mode: TestChannelMode;
  onModeChange: (mode: TestChannelMode) => void;
  /** How many sample answers the build carries (shown in Mock). */
  sampleCount: number;
}

const MODES: Array<{ value: TestChannelMode; label: string }> = [
  { value: 'live', label: 'Live' },
  { value: 'mock', label: 'Mock' },
];

export function TestChannelBanner({ mode, onModeChange, sampleCount }: TestChannelBannerProps) {
  const detail =
    mode === 'mock'
      ? sampleCount > 0
        ? `AI answers are Claude's ${sampleCount} sample${sampleCount === 1 ? '' : 's'}; nothing is charged.`
        : 'AI answers are placeholders (this build has no samples); nothing is charged.'
      : 'AI calls are real and use your credits.';
  return (
    <div
      role="status"
      data-testid="forge-test-channel-banner"
      className="flex flex-shrink-0 flex-wrap items-center justify-center gap-x-3 gap-y-1 border-b border-amber-300 bg-amber-50 px-3 py-1.5 text-xs text-amber-900 dark:border-amber-700/60 dark:bg-amber-950/40 dark:text-amber-200"
    >
      <span className="font-medium">Test build — only you can see this</span>
      <div
        role="radiogroup"
        aria-label="How calls that cost credits behave"
        className="inline-flex overflow-hidden rounded border border-amber-400 dark:border-amber-700"
      >
        {MODES.map(({ value, label }) => (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={mode === value}
            data-testid={`forge-test-channel-mode-${value}`}
            onClick={() => onModeChange(value)}
            className={cn(
              'px-2 py-0.5 font-medium transition-colors',
              mode === value
                ? 'bg-amber-600 text-white dark:bg-amber-500 dark:text-amber-950'
                : 'hover:bg-amber-100 dark:hover:bg-amber-900/50',
            )}
          >
            {label}
          </button>
        ))}
      </div>
      <span data-testid="forge-test-channel-mode-detail">{detail}</span>
    </div>
  );
}
